import React from 'react';
import { Box, Typography, Button, Paper, Divider, Stack, Collapse, IconButton } from '@mui/material';
import { Home, Refresh, BugReport, ExpandMore, ExpandLess } from '@mui/icons-material';
import { ErrorReportDialog } from './ErrorReportDialog';
import { isChunkLoadError, reloadForNewDeploy, clearReloadMark } from '../../utils/lazyWithRetry';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  showDetails: boolean;
  reportOpen: boolean;
  /** 배포 교체로 옛 chunk 가 사라져 발생한 로딩 실패인지 */
  isChunkError: boolean;
  /** 자동 새로고침을 실제로 시작했는지 (안내 문구 분기용) */
  autoReloading: boolean;
}

class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false, error: null, errorInfo: null, showDetails: false, reportOpen: false,
      isChunkError: false, autoReloading: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);

    // 배포 교체로 옛 chunk 가 사라진 경우: 사용자가 아무것도 하지 않아도 새 번들로 복구시킨다.
    // (lazyWithRetry 가 이미 시도했다면 세션 가드에 막혀 false → 안내 화면만 남는다)
    if (isChunkLoadError(error) && reloadForNewDeploy()) {
      this.setState({ autoReloading: true });
    }
  }

  handleReload = () => {
    // 수동 새로고침은 사용자의 명시적 의사이므로 자동 리로드 가드를 풀어준다
    clearReloadMark();
    this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false, autoReloading: false });
    window.location.reload();
  };

  handleGoHome = () => {
    clearReloadMark();
    this.setState({ hasError: false, error: null, errorInfo: null, isChunkError: false, autoReloading: false });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorInfo, showDetails, reportOpen, isChunkError, autoReloading } = this.state;

    // 배포 교체로 화면 파일이 바뀐 경우 — 시스템 오류가 아니라 "새 버전 안내" 로 보여준다.
    if (isChunkError) {
      return (
        <Box
          data-testid="chunk-error-boundary"
          sx={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, var(--ws-subtle) 0%, var(--ws-blue-bg) 100%)', p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{ maxWidth: 520, width: '100%', textAlign: 'center', p: { xs: 4, md: 6 }, borderRadius: 4, border: '1px solid var(--ws-blue-line)' }}
          >
            <Typography variant="h5" fontWeight={700} gutterBottom>
              새 버전이 배포되었습니다
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {autoReloading
                ? '최신 화면을 불러오는 중입니다. 잠시만 기다려 주세요…'
                : '화면 파일이 갱신되어 지금 페이지를 표시할 수 없습니다. 새로고침하면 최신 화면으로 이어집니다.'}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={this.handleReload}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                새로고침
              </Button>
              {typeof window !== 'undefined' && localStorage.getItem('auth_token') && (
                <Button
                  variant="outlined"
                  startIcon={<Home />}
                  onClick={this.handleGoHome}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  대시보드로 이동
                </Button>
              )}
            </Stack>
          </Paper>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, var(--ws-red-bg) 0%, var(--ws-red-bg) 100%)',
          p: 3,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            textAlign: 'center',
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            border: '1px solid var(--ws-red-line)',
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: { xs: 60, md: 80 },
                fontWeight: 900,
                lineHeight: 1,
                background: 'linear-gradient(135deg, var(--ws-red) 0%, var(--ws-red) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              OOPS!
            </Typography>
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            예상치 못한 오류가 발생했습니다
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            문제가 발생하여 페이지를 표시할 수 없습니다. 새로고침하거나 대시보드로 이동해 주세요.
          </Typography>

          {error && (
            <Box sx={{ mb: 3 }}>
              <Button
                size="small"
                onClick={() => this.setState({ showDetails: !showDetails })}
                endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}
                sx={{ textTransform: 'none', color: 'var(--ws-ink-3)' }}
              >
                기술적 세부 정보
              </Button>
              <Collapse in={showDetails}>
                <Box
                  sx={{
                    mt: 1,
                    p: 2,
                    bgcolor: '#1e1e1e',
                    borderRadius: 2,
                    textAlign: 'left',
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ws-red)', mb: 1 }}>
                    {error.name}: {error.message}
                  </Typography>
                  {errorInfo?.componentStack && (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ws-ink-4)', whiteSpace: 'pre-wrap' }}>
                      {errorInfo.componentStack.slice(0, 500)}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          <Divider sx={{ mb: 3 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            {/* 고객 노출 경로(/track/*, /homestyling-apply)에서는 "대시보드로 이동" 무조건 숨김.
                그 외 경로는 관리자 로그인(localStorage.auth_token) 상태에서만 노출. */}
            {(() => {
              if (typeof window === 'undefined') return null;
              const path = window.location.pathname || '';
              const isCustomerPath = path.startsWith('/track') || path.startsWith('/homestyling-apply') || path.startsWith('/repair-apply') || path.startsWith('/agree');
              if (isCustomerPath) return null;
              if (!localStorage.getItem('auth_token')) return null;
              return (
                <Button
                  variant="contained"
                  startIcon={<Home />}
                  onClick={this.handleGoHome}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  대시보드로 이동
                </Button>
              );
            })()}
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={this.handleReload}
              sx={{ borderRadius: 2, textTransform: 'none' }}
            >
              새로고침
            </Button>
            <Button
              variant="text"
              startIcon={<BugReport />}
              onClick={() => this.setState({ reportOpen: true })}
              sx={{ borderRadius: 2, textTransform: 'none', color: 'var(--ws-red)' }}
            >
              오류 리포트
            </Button>
          </Stack>
        </Paper>

        <ErrorReportDialog
          open={reportOpen}
          onClose={() => this.setState({ reportOpen: false })}
          errorType="runtime"
          errorMessage={error?.message || '알 수 없는 오류'}
          errorStack={error?.stack}
          componentStack={errorInfo?.componentStack || undefined}
          errorPath={window.location.pathname}
        />
      </Box>
    );
  }
}

export default ErrorBoundary;
